import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { functions } from '../../lib/firebase';

/**
 * admin ブラウザ向け Google Maps JS APIキーの取得 (docs/25 §7-2/§11、Increment 3、
 * `getStreetViewBrowserConfig` callable)。district単位の in-memory cache で再取得を避ける
 * (メモリのみ、リロードで消える。`useFetchStreetViewImages.ts` と同じパターン)。
 */

export type StreetViewBrowserKeyState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; apiKey: string }
  | { status: 'disabled' }
  | { status: 'error'; error: string };

interface GetStreetViewBrowserConfigResult {
  ok: boolean;
  apiKey?: string;
  error?: string;
}

const _cache = new Map<string, string>();

export function useStreetViewBrowserKey(districtId: string | null): StreetViewBrowserKeyState {
  const [state, setState] = useState<StreetViewBrowserKeyState>({ status: 'idle' });

  useEffect(() => {
    if (!districtId) {
      setState({ status: 'idle' });
      return;
    }
    const cached = _cache.get(districtId);
    if (cached) {
      setState({ status: 'ready', apiKey: cached });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      const callable = httpsCallable<{ districtId: string }, GetStreetViewBrowserConfigResult>(
        functions,
        'getStreetViewBrowserConfig',
      );
      const res = await callable({ districtId });
      if (cancelled) return;
      if (res.data.ok && res.data.apiKey) {
        _cache.set(districtId, res.data.apiKey);
        setState({ status: 'ready', apiKey: res.data.apiKey });
      } else if (res.data.error === 'streetview-3d-disabled') {
        setState({ status: 'disabled' });
      } else {
        setState({ status: 'error', error: res.data.error ?? 'キー取得に失敗しました' });
      }
    })().catch((e: unknown) => {
      if (!cancelled) {
        setState({ status: 'error', error: e instanceof Error ? e.message : String(e) });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [districtId]);

  return state;
}
